import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from "@floating-ui/react";
import { Editor, getMarkRange, posToDOMRect } from "@tiptap/core";
import { useEffect, useRef, useState } from "react";
import { LinkEditorCard, LinkEditorMode } from "./LinkEditorCard";
import { normalizeLinkHref } from "./linkUtils";

interface LinkBubbleState {
  from: number;
  to: number;
  mode: LinkEditorMode;
}

interface LinkBubbleProps {
  editor: Editor | null;
}

/**
 * Floating link card that opens when an existing link is clicked in the
 * document (openOnClick is off, so clicks only set the caret). Shows the link
 * in preview mode — open in new tab / edit / remove — right next to the link
 * itself; the pencil flips the card to edit mode in place.
 */
export const LinkBubble = ({ editor }: LinkBubbleProps) => {
  const [state, setState] = useState<LinkBubbleState | null>(null);
  const [urlValue, setUrlValue] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { refs, floatingStyles } = useFloating({
    open: !!state,
    placement: "bottom",
    strategy: "fixed",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Anchor to the link's document positions via a virtual element (the same
  // approach as TipTap's BubbleMenu). Anchoring to the clicked <a> node breaks:
  // ProseMirror redraws (selection decorations, collab cursors) can replace it
  // at any time, and a disconnected node measures as a 0x0 rect at (0,0).
  useEffect(() => {
    if (!editor || !state) return;
    refs.setPositionReference({
      getBoundingClientRect: () => posToDOMRect(editor.view, state.from, state.to),
      contextElement: editor.view.dom,
    });
  }, [editor, state, refs]);

  // Open the card when a link inside the editor is clicked
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const dom = editor.view.dom;
    // At most one deferred open decision is pending at a time; any newer click
    // supersedes it so a stale timeout can't overwrite a later card.
    let cancelPendingDecide: (() => void) | null = null;

    const handleClick = (event: MouseEvent) => {
      cancelPendingDecide?.();
      cancelPendingDecide = null;

      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest("a");
      if (!anchor || !dom.contains(anchor)) return;

      // Editor links render with target="_blank", and openOnClick:false only
      // removes TipTap's own click handler — the browser default can still
      // navigate. A click on a link opens the bubble, never the URL; the
      // bubble's preview carries the explicit Open affordance.
      event.preventDefault();

      let range: { from: number; to: number } | null = null;
      try {
        const pos = editor.view.posAtDOM(anchor, 0);
        if (pos >= 0) {
          range = getMarkRange(editor.state.doc.resolve(pos), editor.state.schema.marks.link) ?? null;
        }
      } catch {
        // The clicked element couldn't be mapped to a document position
      }
      if (!range) return;
      const linkRange = range;
      const href = anchor.getAttribute("href") ?? "";

      // A text selection (drag or double-click) belongs to the highlight menu,
      // so only open for a caret — and only if the caret actually settled
      // inside the clicked link (not e.g. after a follow-up click elsewhere).
      const openIfCaret = () => {
        if (editor.isDestroyed || !editor.state.selection.empty) return;
        const caret = editor.state.selection.from;
        if (caret < linkRange.from || caret > linkRange.to) return;
        setUrlValue(href);
        setError(false);
        setState({ from: linkRange.from, to: linkRange.to, mode: "preview" });
      };

      // When the click lands on an existing selection, both the DOM selection
      // and editor.state still report the old selection during the click event
      // — ProseMirror collapses it to a caret asynchronously. Wait for that
      // selectionUpdate before deciding (with a timeout in case the selection
      // doesn't change at all).
      const domSelection = dom.ownerDocument.defaultView?.getSelection();
      const selectionSettled = (!domSelection || domSelection.isCollapsed) && editor.state.selection.empty;
      if (selectionSettled) {
        openIfCaret();
        return;
      }

      let decided = false;
      const decide = () => {
        if (decided) return;
        decided = true;
        editor.off("selectionUpdate", decide);
        openIfCaret();
      };
      const timeoutId = setTimeout(decide, 150);
      editor.on("selectionUpdate", decide);
      cancelPendingDecide = () => {
        decided = true;
        clearTimeout(timeoutId);
        editor.off("selectionUpdate", decide);
      };
    };

    dom.addEventListener("click", handleClick);
    return () => {
      cancelPendingDecide?.();
      dom.removeEventListener("click", handleClick);
    };
  }, [editor]);

  // Close on outside mousedown; clicks on the card or on a link are handled elsewhere
  useEffect(() => {
    if (!state) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      if (target.closest("[data-link-bubble]")) return;
      if (target.closest("a") && editor?.view.dom.contains(target)) return;
      setState(null);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [state, editor]);

  // Close when the document changes underneath the card (collab edits, undo, …)
  // — the saved positions would go stale — or when a text selection appears
  // (the highlight menu takes over, e.g. double-clicking a word in the link).
  useEffect(() => {
    if (!editor || !state) return;

    const handleTransaction = ({ transaction }: { transaction: { docChanged: boolean } }) => {
      if (transaction.docChanged || !editor.state.selection.empty) {
        setState(null);
      }
    };

    editor.on("transaction", handleTransaction);
    return () => {
      editor.off("transaction", handleTransaction);
    };
  }, [editor, state]);

  // Escape closes the card in preview mode (the edit input handles its own keys)
  useEffect(() => {
    if (!state || state.mode !== "preview") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setState(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [state]);

  // Focus + select the URL input when flipping to edit mode
  useEffect(() => {
    if (state?.mode !== "edit") return;
    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, [state?.mode]);

  const applyLink = () => {
    const href = normalizeLinkHref(urlValue);
    if (!href || !editor || !state) {
      setError(true);
      return;
    }

    const ok = editor.chain().focus().setTextSelection({ from: state.from, to: state.to }).extendMarkRange("link").setLink({ href }).run();
    if (ok) {
      setState(null);
    } else {
      setError(true);
    }
  };

  const removeLink = () => {
    if (!editor || !state) return;
    editor.chain().focus().setTextSelection({ from: state.from, to: state.to }).extendMarkRange("link").unsetLink().run();
    setState(null);
  };

  const closeAndRefocusEditor = () => {
    setState(null);
    editor?.commands.focus();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      applyLink();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndRefocusEditor();
    }
  };

  if (!state) return null;

  return (
    <FloatingPortal>
      {/* Positioning and animation must stay on separate elements: the flyout
          keyframes animate transform with forwards fill, which would override
          floating-ui's inline translate and pin the card to the viewport origin. */}
      <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 100 }} data-link-bubble>
        <LinkEditorCard
          className="origin-top animate-menu-pop motion-reduce:animate-none"
          mode={state.mode}
          url={urlValue}
          error={error}
          showRemove
          inputRef={inputRef}
          onUrlChange={(value) => {
            setUrlValue(value);
            setError(false);
          }}
          onInputKeyDown={handleInputKeyDown}
          onApply={applyLink}
          onStartEdit={() => setState((s) => (s ? { ...s, mode: "edit" } : s))}
          onRemove={removeLink}
        />
      </div>
    </FloatingPortal>
  );
};
